import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { UploadSimple, X, Image as ImageIcon, Trash, Camera, FilePdf, CheckSquare, Square, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getToken } from "@/lib/authToken";
import FolderBrowser from "./FolderBrowser";
const fmtSize = (b) => (b > 1024 * 1024 ? (b / 1024 / 1024).toFixed(1) + " MB" : (b / 1024).toFixed(0) + " KB");

export default function PhotosTab({ expedienteId, onCountChange }) {
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [size, setSize] = useState("1x"); // 1x | 2x | 3x
  const [preview, setPreview] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [exportingPdf, setExportingPdf] = useState(false);
  const [currentFolder, setCurrentFolder] = useState("");
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const token = getToken();

  const load = async () => {
    const r = await api.get(`/expedientes/${expedienteId}/attachments?kind=photo`);
    setItems(r.data);
    onCountChange?.();
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [expedienteId]);

  // Mientras haya fotos con caption_ai_status === "pending" (subiendo o
  // esperando que Gemini sugiera el pie de foto), refrescamos cada 3 s.
  // En cuanto todas terminan, paramos el polling.
  useEffect(() => {
    const anyPending = items.some((p) => p.caption_ai_status === "pending");
    if (!anyPending) return undefined;
    const t = setInterval(() => { load(); }, 3000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [items]);

  const moveItem = async (attId, folder) => {
    await api.patch(`/expedientes/${expedienteId}/attachments/${attId}`, { folder });
    load();
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => {
        if (f.type.startsWith("image/")) fd.append("files", f);
      });
      fd.append("kind", "photo");
      if (currentFolder) fd.append("folder", currentFolder);
      const { data } = await api.post(
        `/expedientes/${expedienteId}/attachments/upload`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 5 * 60 * 1000, // 5 min — fotos pesadas tardan
        },
      );
      // El endpoint devuelve `failed[]` si alguna foto no se pudo subir
      // (timeout del ingress, fichero truncado, etc.). Avisamos al perito
      // para que pueda re-subir SOLO las que fallaron.
      const ok = data?.created || 0;
      const ko = data?.failed?.length || 0;
      if (ko > 0) {
        const names = data.failed.map((f) => f.filename).slice(0, 5).join(", ");
        toast.error(
          `Se subieron ${ok} de ${ok + ko}. Reintenta: ${names}${ko > 5 ? "…" : ""}`,
          { duration: 8000 },
        );
      } else if (ok > 0) {
        toast.success(`${ok} foto${ok === 1 ? "" : "s"} subida${ok === 1 ? "" : "s"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo fotos");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files);
  };

  const del = async (id) => {
    if (!confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/expedientes/${expedienteId}/attachments/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    }
  };

  // -------- Selección para PDF
  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectMode = () => {
    setSelectMode((m) => {
      if (m) setSelected(new Set());
      return !m;
    });
  };

  const selectAll = () => {
    setSelected(new Set(items.map((p) => p.id)));
  };

  const clearSelection = () => setSelected(new Set());

  // Generar PDF de las fotos seleccionadas (orden = el del grid)
  const exportPdf = async () => {
    if (!selected.size || exportingPdf) return;
    setExportingPdf(true);
    try {
      const ordered_ids = items.filter((p) => selected.has(p.id)).map((p) => p.id);
      const resp = await api.post(
        `/expedientes/${expedienteId}/fotos/pdf`,
        { photo_ids: ordered_ids },
        { responseType: "blob" },
      );
      // Extraer filename de Content-Disposition si existe
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : "fotos.pdf";
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(`PDF generado con ${ordered_ids.length} foto${ordered_ids.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const totalSize = items.reduce((a, i) => a + (i.size_bytes || 0), 0);

  const sizes = {
    "1x": "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6",
    "2x": "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
    "3x": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  };

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="p-3 md:p-6 max-w-6xl">
        {/* Botones de acción para móvil: cámara + galería */}
        <div className="md:hidden flex gap-2 mb-3">
          <button
            onClick={() => cameraRef.current?.click()}
            data-testid="photos-mobile-camera"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-foreground text-white text-[13px] font-semibold rounded-md py-3"
          >
            <Camera className="w-4 h-4" weight="fill" />
            Cámara
          </button>
          <button
            onClick={() => inputRef.current?.click()}
            data-testid="photos-mobile-gallery"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-border text-foreground text-[13px] font-semibold rounded-md py-3"
          >
            <ImageIcon className="w-4 h-4" weight="bold" />
            Galería
          </button>
          {uploading && <div className="dot-blink text-muted self-center"><span/><span/><span/></div>}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => upload(e.target.files)}
            className="hidden"
            data-testid="photos-camera-input"
          />
        </div>

        {/* Drop zone (desktop) */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`hidden md:flex border-2 border-dashed rounded-lg p-6 cursor-pointer transition-all items-center gap-4 ${
            drag ? "border-foreground bg-surface-2" : "border-border bg-surface hover:bg-surface-2"
          }`}
          data-testid="photos-dropzone"
        >
          <UploadSimple className="w-7 h-7 text-muted flex-shrink-0" weight="bold" />
          <div className="flex-1">
            <div className="font-semibold text-sm">Subir fotos</div>
            <div className="text-xs text-muted">JPG, PNG, HEIC, WebP... Arrastra aquí o haz clic. Hasta 50 a la vez.</div>
          </div>
          {uploading && <div className="dot-blink text-muted"><span/><span/><span/></div>}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => upload(e.target.files)}
            className="hidden"
            data-testid="photos-file-input"
          />
        </div>

        {/* Toolbar */}
        <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-sm">
            <span className="font-semibold">{items.length} foto{items.length === 1 ? "" : "s"}</span>
            <span className="text-muted ml-2">· {fmtSize(totalSize)}</span>
            {selectMode && (
              <span className="ml-3 text-brand font-semibold" data-testid="photos-selected-count">
                {selected.size} seleccionada{selected.size === 1 ? "" : "s"}
              </span>
            )}
          </h2>
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            {/* Selector de modo selección + PDF */}
            {!selectMode ? (
              <button
                onClick={toggleSelectMode}
                disabled={items.length === 0}
                data-testid="photos-select-mode-toggle"
                className="text-[12px] px-2.5 h-7 border border-border bg-white rounded-md hover:bg-background inline-flex items-center gap-1.5 font-semibold disabled:opacity-40"
                title="Selecciona fotos para generar un PDF"
              >
                <FilePdf className="w-3.5 h-3.5" weight="duotone" />
                Crear PDF de fotos
              </button>
            ) : (
              <>
                <button
                  onClick={selectAll}
                  data-testid="photos-select-all"
                  className="text-[12px] px-2.5 h-7 border border-border bg-white rounded-md hover:bg-background font-semibold"
                >
                  Todas
                </button>
                <button
                  onClick={clearSelection}
                  data-testid="photos-clear-selection"
                  disabled={selected.size === 0}
                  className="text-[12px] px-2.5 h-7 border border-border bg-white rounded-md hover:bg-background font-semibold disabled:opacity-40"
                >
                  Ninguna
                </button>
                <button
                  onClick={exportPdf}
                  disabled={selected.size === 0 || exportingPdf}
                  data-testid="photos-export-pdf"
                  className="text-[12px] px-3 h-7 bg-brand text-white rounded-md hover:bg-brand/90 inline-flex items-center gap-1.5 font-semibold disabled:opacity-40"
                >
                  <FilePdf className="w-3.5 h-3.5" weight="fill" />
                  {exportingPdf ? "Generando..." : `Generar PDF (${selected.size})`}
                </button>
                <button
                  onClick={toggleSelectMode}
                  data-testid="photos-cancel-selection"
                  className="text-[12px] px-2.5 h-7 border border-border bg-white rounded-md hover:bg-background"
                  title="Cancelar selección"
                >
                  <X className="w-3.5 h-3.5" weight="bold" />
                </button>
              </>
            )}
            <span className="text-muted mx-1">·</span>
            <span className="text-muted mr-1">Tamaño</span>
            {["1x", "2x", "3x"].map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                data-testid={`size-${s}`}
                className={`w-8 h-7 rounded-md font-mono font-semibold border transition-colors ${
                  size === s ? "bg-foreground text-white border-foreground" : "bg-white text-muted border-border hover:text-foreground"
                }`}
              >{s}</button>
            ))}
          </div>
        </div>

        {/* Grid con sidebar de carpetas + drag&drop. Se muestra siempre,
            incluso sin fotos, para poder crear carpetas y luego subirlas. */}
        <div className="mt-3 min-h-[300px]">
          <FolderBrowser
            expedienteId={expedienteId}
            kind="photo"
            items={items}
            onMoveItem={moveItem}
            renderGrid={(filtered, folder) =>
              filtered.length === 0 ? (
                <div className="text-[12.5px] text-muted italic text-center py-10 bg-white border border-dashed border-border rounded-lg">
                  {items.length === 0
                    ? "Sin fotos todavía. Arrastra imágenes arriba."
                    : folder
                      ? "Carpeta vacía. Arrastra fotos aquí o súbelas desde arriba."
                      : "No hay fotos sin clasificar."}
                </div>
              ) : (
                <div className={`grid gap-3 ${sizes[size]}`}>
                  {filtered.map((p) => (
                    <PhotoCard
                      key={p.id}
                      photo={p}
                      expedienteId={expedienteId}
                      token={token}
                      onPreview={() => setPreview(p)}
                      onDelete={() => del(p.id)}
                      onSaved={load}
                      selectMode={selectMode}
                      isSelected={selected.has(p.id)}
                      onToggleSelect={() => toggleSelected(p.id)}
                    />
                  ))}
                </div>
              )
            }
          />
        </div>
      </div>

      {/* Lightbox */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="photo-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            onClick={() => setPreview(null)}
          >
            <X className="w-6 h-6" weight="bold" />
          </button>
          <img
            src={`${API}/expedientes/${expedienteId}/attachments/${preview.id}/file?token=${token}`}
            alt={preview.filename}
            className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-4 text-white/70 text-xs font-mono">{preview.filename}</div>
        </div>
      )}
    </div>
  );
}


function PhotoCard({ photo, expedienteId, token, onPreview, onDelete, onSaved, selectMode = false, isSelected = false, onToggleSelect }) {
  const [caption, setCaption] = useState(photo.caption || "");
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => { setCaption(photo.caption || ""); }, [photo.id, photo.caption]);

  const aiPending = photo.caption_ai_status === "pending";
  const aiFailed = photo.caption_ai_status === "failed";
  const wasAiSuggested = !!photo.caption_was_ai_suggested;

  const saveCaption = async () => {
    const trimmed = caption.trim();
    if ((trimmed || null) === (photo.caption || null)) return;
    setSaving(true);
    try {
      await api.patch(`/expedientes/${expedienteId}/attachments/${photo.id}`, { caption: trimmed });
      onSaved?.();
    } finally { setSaving(false); }
  };

  const suggestAgain = async () => {
    setSuggesting(true);
    try {
      // force=true sólo si el caption actual fue editado a mano (para
      // que el perito tenga que confirmar la sobrescritura). Si era IA
      // o estaba vacío, lo aplicamos directamente.
      const force = !!(caption.trim() && !wasAiSuggested);
      if (force && !window.confirm("¿Sobrescribir tu pie de foto con la nueva sugerencia de la IA?")) {
        setSuggesting(false);
        return;
      }
      const { data } = await api.post(
        `/expedientes/${expedienteId}/attachments/${photo.id}/suggest-caption?force=${force}`,
      );
      if (data?.applied) {
        toast.success(`Sugerido: "${data.suggested}"`);
        onSaved?.();
      } else if (data?.suggested) {
        toast.message("Sugerencia IA", { description: `"${data.suggested}" (no aplicada, mantienes el tuyo)` });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo sugerir un pie de foto");
    } finally {
      setSuggesting(false);
    }
  };

  const onImageClick = () => {
    if (selectMode) onToggleSelect?.();
    else onPreview?.();
  };

  return (
    <div
      draggable={!selectMode}
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-att-id", photo.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className={`group bg-white border rounded-lg overflow-hidden flex flex-col transition-colors ${
        selectMode && isSelected ? "border-brand ring-2 ring-brand/40" : "border-border"
      } ${!selectMode ? "cursor-move" : ""}`}
      data-testid={`photo-card-${photo.id}`}
    >
      <div className="relative aspect-[4/3] bg-surface overflow-hidden">
        <img
          src={`${API}/expedientes/${expedienteId}/attachments/${photo.id}/file?token=${token}`}
          alt={photo.caption || photo.filename}
          loading="lazy"
          onClick={onImageClick}
          className={`w-full h-full object-cover transition-transform group-hover:scale-[1.02] ${
            selectMode ? "cursor-pointer" : "cursor-zoom-in"
          }`}
        />
        {selectMode && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
            data-testid={`photo-select-${photo.id}`}
            className={`absolute top-1.5 left-1.5 w-7 h-7 rounded-md flex items-center justify-center transition-all ${
              isSelected
                ? "bg-brand text-white shadow-md"
                : "bg-white/90 text-foreground border border-border hover:bg-white"
            }`}
            title={isSelected ? "Quitar de la selección" : "Añadir a la selección"}
          >
            {isSelected ? <CheckSquare className="w-4 h-4" weight="fill" /> : <Square className="w-4 h-4" weight="regular" />}
          </button>
        )}
        {!selectMode && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            data-testid={`delete-photo-${photo.id}`}
            className="absolute top-1.5 right-1.5 p-1 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-all"
          >
            <Trash className="w-3 h-3" weight="bold" />
          </button>
        )}
      </div>
      <div className="p-2 border-t border-border">
        <div className="relative">
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            onBlur={saveCaption}
            placeholder={aiPending ? "Generando pie de foto…" : "Pie de foto para el informe…"}
            maxLength={280}
            disabled={aiPending && !caption}
            data-testid={`caption-${photo.id}`}
            className={`w-full !text-[12px] !py-1 !pl-2 !pr-7 border-none focus:ring-1 rounded ${
              wasAiSuggested ? "focus:ring-violet-400 bg-violet-50/40" : "focus:ring-amber-400"
            } ${aiPending ? "italic text-muted" : ""}`}
          />
          {/* Botón "volver a sugerir" (estrella) */}
          {!aiPending && (
            <button
              onClick={(e) => { e.stopPropagation(); suggestAgain(); }}
              disabled={suggesting}
              data-testid={`suggest-caption-${photo.id}`}
              title={
                aiFailed
                  ? "La IA no pudo sugerir un pie de foto. Reintentar"
                  : wasAiSuggested
                  ? "Volver a sugerir con IA"
                  : "Sugerir pie de foto con IA"
              }
              className={`absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
                suggesting
                  ? "text-violet-500 animate-spin"
                  : aiFailed
                  ? "text-rose-500 hover:bg-rose-50"
                  : wasAiSuggested
                  ? "text-violet-500 hover:bg-violet-50"
                  : "text-muted hover:text-violet-500 hover:bg-violet-50"
              }`}
            >
              {suggesting ? <ArrowsClockwise className="w-3 h-3" weight="bold" /> : <Sparkle className="w-3 h-3" weight="fill" />}
            </button>
          )}
          {aiPending && (
            <ArrowsClockwise
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-violet-500 animate-spin"
              weight="bold"
              data-testid={`caption-loading-${photo.id}`}
            />
          )}
        </div>
        <div className="flex items-center justify-between mt-0.5 pl-2 min-h-[12px]">
          {saving && <div className="text-[9px] text-muted">Guardando…</div>}
          {!saving && wasAiSuggested && (
            <div className="text-[9px] text-violet-700 inline-flex items-center gap-1" data-testid={`caption-ai-badge-${photo.id}`}>
              <Sparkle className="w-2.5 h-2.5" weight="fill" /> Sugerido por IA
            </div>
          )}
          {!saving && aiFailed && !caption && (
            <div className="text-[9px] text-rose-600">IA no disponible</div>
          )}
        </div>
      </div>
    </div>
  );
}
